import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

interface LikedByUser {
  user_id: string;
  full_name?: string;
  email: string;
  avatar_url?: string;
}

interface VideoLikedByProps {
  videoId: string;
  likes: LikedByUser[];
  totalLikes: number;
  loading?: boolean;
  variant?: 'full' | 'compact';
  maxAvatars?: number;
}

const VideoLikedBy: React.FC<VideoLikedByProps> = ({
  videoId,
  likes,
  totalLikes,
  loading = false,
  variant = 'full',
  maxAvatars = 4
}) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2">
          {Array.from({ length: Math.min(maxAvatars, 3) }).map((_, i) => (
            <Skeleton key={i} className="w-8 h-8 rounded-full border-2 border-background" />
          ))}
        </div>
        <Skeleton className="h-4 w-24" />
      </div>
    );
  }

  if (totalLikes === 0) {
    return null;
  }

  const displayedLikes = likes.slice(0, maxAvatars);
  const remainingCount = Math.max(0, totalLikes - maxAvatars);

  const getUserDisplayName = (user: LikedByUser): string => {
    return user.full_name || user.email?.split('@')[0] || 'User';
  };

  const getUserInitials = (user: LikedByUser): string => {
    const name = getUserDisplayName(user);
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatLikedByText = (): string => {
    if (totalLikes === 1) {
      return `${getUserDisplayName(likes[0])} liked this`;
    }

    if (totalLikes === 2) {
      return `${getUserDisplayName(likes[0])} and ${getUserDisplayName(likes[1])} liked this`;
    }

    if (totalLikes === 3 && maxAvatars >= 3) {
      return `${getUserDisplayName(likes[0])}, ${getUserDisplayName(likes[1])} and ${getUserDisplayName(likes[2])} liked this`;
    }

    // For 3+ likes or when we're showing fewer avatars
    if (remainingCount > 0) {
      return `${getUserDisplayName(likes[0])} and ${remainingCount + (maxAvatars > 1 ? maxAvatars - 1 : 0)} others liked this`;
    }

    // Fallback for edge cases
    return `${totalLikes} ${totalLikes === 1 ? 'person' : 'people'} liked this`;
  };

  const avatarSize = variant === 'compact' ? 'w-6 h-6' : 'w-8 h-8';
  const textSize = variant === 'compact' ? 'text-xs' : 'text-sm';
  const spacing = variant === 'compact' ? '-space-x-1' : '-space-x-2';

  return (
    <div className="flex items-center gap-2">
      {/* Avatar Stack */}
      <div className={`flex ${spacing}`}>
        {displayedLikes.map((user, index) => (
          <Avatar
            key={user.user_id}
            className={`${avatarSize} border-2 border-background relative z-${10 + index}`}
            title={getUserDisplayName(user)}
          >
            <AvatarImage 
              src={user.avatar_url} 
              alt={getUserDisplayName(user)}
            />
            <AvatarFallback className="text-xs">
              {getUserInitials(user)}
            </AvatarFallback>
          </Avatar>
        ))}
        
        {/* Show +N indicator if there are more likes than displayed avatars */}
        {remainingCount > 0 && variant === 'full' && (
          <div 
            className={`${avatarSize} border-2 border-background bg-muted flex items-center justify-center rounded-full text-xs font-medium text-muted-foreground relative z-${10 + displayedLikes.length}`}
            title={`${remainingCount} more ${remainingCount === 1 ? 'person' : 'people'} liked this`}
          >
            +{remainingCount}
          </div>
        )}
      </div>

      {/* Liked By Text */}
      <span className={`${textSize} text-muted-foreground`}>
        {formatLikedByText()}
      </span>
    </div>
  );
};

export default VideoLikedBy;